import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InputBar from './InputBar';
import { useChat } from './ChatContext';
import { ChatResponse } from '../types/api';
import { v4 as uuidv4 } from 'uuid';
import ImageModal from './ImageModal';

export default function ChatWindow() {
  const { currentChat, addMessage, loadChat } = useChat();
  const [currentVizId, setCurrentVizId] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [thinkingDots, setThinkingDots] = React.useState<string>("");
  const [modalImage, setModalImage] = React.useState<string | null>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const lastMessageRef = React.useRef<HTMLDivElement>(null);

  const handleSend = async (text: string): Promise<void> => {
    // 1) add the user's message
    addMessage(text, 'user');

    // 3) call backend (using relative URL, ensure proxy is set in package.json)
    setIsLoading(true);
    try {
      const resp = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, visualizationId: currentVizId })
      });
      console.log('Chat API response status:', resp.status);
      const data: ChatResponse = await resp.json();
      if (!resp.ok) {
        console.error('Chat API error response:', data);
        throw new Error((data as any).error || 'API returned an error');
      }

      // 4) add the assistant's reply
      console.log('Chat API reply object:', data.reply);
      
      // Process the reply content
      if (typeof data.reply.content === 'string') {
        // If it's a string, treat it as text
        addMessage(data.reply.content, 'assistant');
      } else if (Array.isArray(data.reply.content)) {
        // Convert backend content format to frontend format
        const messageContent = data.reply.content.map(item => {
          if (item.type === 'text' && item.text) {
            return { type: 'text' as const, text: item.text };
          } else if (item.type === 'image' && item.image?.data) {
            return { type: 'image' as const, imageData: item.image.data };
          }
          return { type: 'text' as const, text: 'Unsupported content type' };
        });
        addMessage(messageContent, 'assistant');
      } else {
        // Fallback
        addMessage('Received an unsupported response format from the AI.', 'assistant');
      }
    } catch (e) {
      console.error('Error in handleSend:', e);
      addMessage([{ type: 'text' as const, text: '🛑 Oops, something went wrong.' }], 'assistant');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const handleVizChange = async (event: Event) => {
      const e = event as CustomEvent<{ visualizationId: string; visualizationName: string }>;
      const { visualizationId, visualizationName } = e.detail;
      try {
        const resp = await fetch(`/api/visualizations?visualizationid=${visualizationId}`);
        if (!resp.ok) return;
        const viz = await resp.json();
        const disp = viz.visualizationDisplayMessages;
        if (Array.isArray(disp) && disp.length > 0) {
          const msgs = disp.map((m: any) => ({
            from: m.role === 'assistant' ? 'assistant' as const : 'user' as const,
            content: typeof m.content === 'string' 
              ? [{ type: 'text' as const, text: m.content }]
              : Array.isArray(m.content) 
                ? m.content.map((item: any) => {
                    if (item.type === 'text' && item.text) {
                      return { type: 'text' as const, text: item.text };
                    } else if (item.type === 'image' && item.image?.data) {
                      return { type: 'image' as const, imageData: item.image.data };
                    }
                    return { type: 'text' as const, text: 'Unsupported content type' };
                  })
                : [{ type: 'text' as const, text: 'Unknown content format' }]
          }));
          loadChat({ id: visualizationId, title: visualizationName, messages: msgs });
        } else {
          loadChat({ id: visualizationId, title: visualizationName, messages: [] });
        }
        setCurrentVizId(visualizationId);
      } catch (err) {
        console.error('Error loading visualization chat:', err);
      }
    };
    window.addEventListener('visualizationChanged', handleVizChange);
    return () => window.removeEventListener('visualizationChanged', handleVizChange);
  }, [loadChat]);

  // Animate dots when loading
  React.useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setThinkingDots(prev => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setThinkingDots('');
    }
  }, [isLoading]);
  
  // Clear chat when project changes
  React.useEffect(() => {
    const handleProjectChange = () => {
      setCurrentVizId(null);
      // reset to a new blank chat
      loadChat({ id: uuidv4(), title: 'New Chat', messages: [] });
    };
    window.addEventListener('projectChanged', handleProjectChange);
    return () => window.removeEventListener('projectChanged', handleProjectChange);
  }, [loadChat]);

  // Auto-scroll to bottom on new messages or loading state change
  React.useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [currentChat.messages, isLoading]);

  const noVizSelected = !currentVizId;

  // Handler to show latest visualization image
  const handleLatestVisualization = async () => {
    if (!currentVizId) return;
    try {
      const resp = await fetch(`/api/visualizations?visualizationid=${currentVizId}`);
      if (!resp.ok) {
        console.error('Failed to fetch visualization', resp.status);
        return;
      }
      const viz = await resp.json();
      const imageData = viz.visualizationImageUrl;
      if (imageData) {
        setModalImage(imageData);
      }
    } catch (err) {
      console.error('Error loading latest visualization image:', err);
    }
  };

  return (
    <div className="flex flex-col w-full flex-1 overflow-hidden bg-chatBackground-light dark:bg-chatBackground-dark text-text-light dark:text-text-dark relative">

      {/* Chat messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-2 space-y-4">
        {(currentChat && Array.isArray(currentChat.messages) ? currentChat.messages : []).map((msg, idx, arr) => (
          <div
            ref={idx === arr.length - 1 ? lastMessageRef : undefined}
            key={idx}
            className={`p-3 rounded-lg max-w-fit ${
              msg.from === 'user'
                ? 'bg-primary-light dark:bg-primary-dark text-white ml-auto'
                : 'self-start'
            }`}
          >
            {msg.content.map((content, contentIdx) => (
              <div key={contentIdx} className="mb-2 last:mb-0">
                {content.type === 'text' && content.text && (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.text}</ReactMarkdown>
                )}
                {content.type === 'image' && content.imageData && (
                  <img 
                    src={`data:image/png;base64,${content.imageData}`}
                    alt="AI generated visualization"
                    className="max-w-full rounded"
                  />
                )}
              </div>
            ))}
          </div>
        ))}
        {/* Loading indicator */}
        {isLoading && (
          <div className="p-3 italic text-gray-500">Thinking{thinkingDots}</div>
        )}
      </div>

      {/* Input field only */}
      <div className="px-4 pb-4">
        <InputBar
          onSend={handleSend}
          disabled={isLoading}
          noVizSelected={noVizSelected}
          onLatestVisualization={handleLatestVisualization}
        />
        <ImageModal
          isOpen={!!modalImage}
          imageData={modalImage}
          onClose={() => setModalImage(null)}
        />
      </div>
    </div>
  );
}
